import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Receipt, MapPin, Phone, User, Package, Clock, LayoutGrid } from "lucide-react";
import {
  filaPizzasService,
  PedidoIntegracao,
  PedidoIntegracaoItem,
} from "../services/filaPizzasService";
import { resolveSalaoProducaoLabels } from "../pages/Painel/utils/origemPainel";

interface PedidoOriginalModalProps {
  orderId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Loja do contexto (painel/filas); ajuda admin a achar o recibo do cardápio web. */
  lojaId?: number;
  /** Use valor maior quando aberto sobre outro modal (ex.: z-[10000]). */
  overlayZIndexClass?: string;
}

const PedidoOriginalModal: React.FC<PedidoOriginalModalProps> = ({
  orderId,
  isOpen,
  onClose,
  lojaId,
  overlayZIndexClass = "z-50",
}) => {
  const [pedido, setPedido] = useState<PedidoIntegracao | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPedido = async () => {
      setIsLoading(true);
      try {
        const data = await filaPizzasService.getPedidoOriginal(orderId, lojaId);
        setPedido(data);
      } catch {
        setPedido(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPedido();
  }, [isOpen, orderId, lojaId]);

  const isSalao =
    pedido?.canalOrigem === "salao" || pedido?.orderType === "SALÃO";
  const salaoLabels = useMemo(
    () =>
      pedido && isSalao
        ? resolveSalaoProducaoLabels({
            mesaId: pedido.salaoMesaId,
            comandaId: pedido.salaoComandaId,
            identificador: pedido.customerName,
            displayId: pedido.displayId,
          })
        : null,
    [pedido, isSalao],
  );
  const showCustomerPhone =
    pedido?.customerPhone &&
    !/^0+$/.test(String(pedido.customerPhone).replace(/\D/g, ""));

  if (!isOpen) return null;

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return "—";
    return `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupOptions = (item: PedidoIntegracaoItem) => {
    const groups: Record<string, { name: string; options: string[] }> = {};
    for (const opt of item.options || []) {
      const key = String(opt.option_group_id ?? opt.option_group_name ?? "_");
      if (!groups[key]) {
        groups[key] = { name: opt.option_group_name, options: [] };
      }
      const qty =
        typeof opt.quantity === "number" && Number.isFinite(opt.quantity)
          ? Math.max(1, opt.quantity)
          : 1;
      groups[key].options.push(`${qty}x ${opt.name}`);
    }
    return Object.values(groups);
  };

  const address = pedido?.deliveryAddress;
  const formattedAddress = address
    ? [
        address.streetName,
        address.streetNumber,
        address.complement,
        address.neighborhood,
        address.city,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/50 p-4 ${overlayZIndexClass}`}
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-neutral-100 p-1.5 text-neutral-500 transition hover:bg-neutral-200 hover:text-neutral-700"
        >
          <X size={18} />
        </button>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : !pedido ? (
          <div className="py-20 text-center text-neutral-500">
            Pedido não encontrado
          </div>
        ) : (
          <div className="font-mono text-sm">
            <div className="border-b-2 border-dashed border-neutral-300 bg-neutral-50 px-5 pb-4 pt-5 text-center">
              <Receipt className="mx-auto mb-2 text-primary" size={28} />
              <h3 className="text-base font-bold text-neutral-800">
                PEDIDO #{pedido.displayId || pedido.orderId}
              </h3>
              <div className="mt-1 flex items-center justify-center gap-1 text-xs text-neutral-500">
                <Clock size={12} />
                {formatDate(pedido.createdAt)}
              </div>
              {pedido.orderType && (
                <span className="mt-2 inline-block rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
                  {pedido.orderType}
                </span>
              )}
            </div>

            {(isSalao ||
              pedido.customerName ||
              showCustomerPhone) && (
              <div className="border-b border-dashed border-neutral-200 px-5 py-3">
                {isSalao && salaoLabels ? (
                  <div className="space-y-1">
                    {salaoLabels.mesa ? (
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-700">
                        <LayoutGrid size={13} className="text-neutral-400" />
                        {salaoLabels.mesa}
                      </div>
                    ) : null}
                    {salaoLabels.comandaLabel ? (
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-700">
                        <Receipt size={13} className="text-neutral-400" />
                        {salaoLabels.comandaLabel}
                      </div>
                    ) : null}
                    {salaoLabels.cliente ? (
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-700">
                        <User size={13} className="text-neutral-400" />
                        {salaoLabels.cliente}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  pedido.customerName && (
                    <div className="flex items-center gap-2 text-xs text-neutral-700">
                      <User size={13} className="text-neutral-400" />
                      <span className="font-semibold">{pedido.customerName}</span>
                    </div>
                  )
                )}
                {showCustomerPhone && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
                    <Phone size={13} className="text-neutral-400" />
                    {pedido.customerPhone}
                  </div>
                )}
              </div>
            )}

            {formattedAddress && (
              <div className="border-b border-dashed border-neutral-200 px-5 py-3">
                <div className="flex items-start gap-2 text-xs text-neutral-600">
                  <MapPin
                    size={13}
                    className="mt-0.5 shrink-0 text-neutral-400"
                  />
                  <span>{formattedAddress}</span>
                </div>
              </div>
            )}

            <div className="border-b-2 border-dashed border-neutral-300 px-5 py-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500">
                <Package size={13} />
                Itens do pedido
              </div>
              {(pedido.items as PedidoIntegracaoItem[])?.map((line, idx) => (
                <div key={idx} className="mb-3 last:mb-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className="font-bold text-neutral-800">
                        {line.quantity}x {line.name}
                      </span>
                    </div>
                    <span className="ml-2 shrink-0 font-bold text-neutral-700">
                      {formatCurrency(line.total_price)}
                    </span>
                  </div>

                  {line.observation && (
                    <div className="mt-1 rounded bg-yellow-50 px-2 py-1 text-xs text-yellow-700">
                      Obs: {line.observation}
                    </div>
                  )}

                  {groupOptions(line).map((group, gIdx) => (
                    <div key={gIdx} className="mt-1.5 pl-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                        {group.name}
                      </div>
                      {group.options.map((opt, oIdx) => (
                        <div key={oIdx} className="text-xs text-neutral-600">
                          • {opt}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {pedido.observation && (
              <div className="border-b border-dashed border-neutral-200 px-5 py-3">
                <div className="rounded bg-yellow-50 px-3 py-2 text-xs text-yellow-700">
                  <span className="font-bold">Observação:</span>{" "}
                  {pedido.observation}
                </div>
              </div>
            )}

            <div className="px-5 py-3">
              {pedido.deliveryFee != null && Number(pedido.deliveryFee) > 0 && (
                <div className="flex justify-between text-xs text-neutral-500">
                  <span>Taxa de entrega</span>
                  <span>{formatCurrency(pedido.deliveryFee)}</span>
                </div>
              )}
              <div className="mt-1 flex justify-between border-t border-neutral-200 pt-2 text-sm font-bold text-neutral-800">
                <span>TOTAL</span>
                <span>{formatCurrency(pedido.total)}</span>
              </div>
            </div>

            <div className="border-t-2 border-dashed border-neutral-300 bg-neutral-50 px-5 py-3 text-center text-[10px] text-neutral-400">
              Order ID: {pedido.orderId}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PedidoOriginalModal;
